import { debounce, isNumber } from 'lodash-es';
import { type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import {
  ChatMessageCategory,
  ChatMessagePayloadType,
  CustomChatMessagePayloadType,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { useChatContext, useLastMessage } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import { Loading } from '@/components/loading';
import { genDBChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/utils';
import { DebugTooltip } from '@/scripts/utils/devUtils';
import { serverTime } from '@/contexts/chat/scripts/hooks/useSystemTime/utils';
import type { ReplyMessage, SetReplyMessage } from '@/scripts/hooks/useReply';
import type { Contact } from '@/contexts/contacts/types';
import classes from './index.module.scss';
import { handleDividerMessage } from './utils';
import type { Divider } from './utils';
import MessageRenderer from './components/messageRenderer';
import AffixShortcut from './components/affixShortcut';
import { ShortcutType } from './components/affixShortcut/type';
import ObserverItem from './observer-item';

export interface ChatMessagesProps {
  className?: ClassNames;
  chat: Chat;
  contacts: Contact[];
  replyMessage: ReplyMessage;
  setReplyMessage: SetReplyMessage;
}

const LIMIT = 20;
enum DIRECTION {
  UP = 1,
  DOWN = -1,
}

function ChatMessages({
  className,
  chat,
  contacts,
  replyMessage,
  setReplyMessage,
}: ChatMessagesProps) {
  const {
    chatMessages,
    updateStateMessageAsRead,
    markMessagesAsRead,
    chatsState,
    loadMessages,
    chatMediaCall,
    updateChats,
    chatGroup,
  } = useChatContext();

  const {
    messagesState: { getMessages, getMessage, clearAllMessages, removeMessages },
  } = chatMessages;

  const { lastMessage: chatLastMessage } = useLastMessage(chat);

  const unreadMessagesRef = useRef(new Map<string | number, ChatMessage>());
  const processUnreadArrRef = useRef<ChatMessage[]>([]);

  const [isFriend, setIsFriend] = useState(false);

  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const isInitialLoad = useRef(false);

  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [hasMoreLast, setHasMoreLast] = useState(true);

  const [canInitScroll, setCanInitScroll] = useState(false);

  const [highlightLinked, setHighlightLinked] = useState<string>('');

  const isSelfSendRef = useRef<boolean>(false);
  const isLoadingTop = useRef<boolean>(false);
  const isNearAtBottomRef = useRef<boolean>(false);

  const loadingTopRef = useRef<HTMLDivElement>(null);
  const loadingBottomRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef<HTMLDivElement>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevScrollInfoRef = useRef<Record<string, any>>({
    scrollTop: 0,
    scrollHeight: 0,
  });

  const firstIdRef = useRef<number>(0);
  const lastIdRef = useRef<number>(0);

  const unreadCount = useMemo(() => {
    return chatsState.chatReadCountMap[chat.chat_id] || 0;
  }, [chatsState, chat.chat_id]);

  const mentionMsgs = useMemo(() => {
    return chatsState.chatMentionMap[chat.chat_id] || [];
  }, [chatsState, chat.chat_id]);

  const messages = useMemo(() => {
    return getMessages(chat.chat_id);
  }, [getMessages, chat.chat_id]);

  const chatLastMessageId = useMemo(() => chatLastMessage?.id, [chatLastMessage]);

  const lastMessage = useMemo(() => messages?.at(-1), [messages]);
  // const lastMessage = chatLastMessage;

  const hiddenNewMessages = useMemo(() => {
    if (lastIdRef.current && chat?.last_read_new_message_id) {
      return hasMoreLast && lastIdRef.current < chat?.last_read_new_message_id;
    } else {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.last_read_new_message_id, hasMoreLast, lastMessage]);

  const allMessages = useMemo(() => {
    return messages.length ? messages : ([genDBChatMessage({})] as ChatMessage[]);
  }, [messages]);

  const refsValues = {
    markMessagesAsRead,
    loadMessages,
    updateChats,
    removeMessages,
    getMessage,
    clearAllMessages,
    messages,
    chat,
    chatMediaCall,
    chatsState,
    chatLastMessage,
    chatLastMessageId,
    updateStateMessageAsRead,
  };

  const refs = useRefs<typeof refsValues & { loadData?: any }>(refsValues);

  const processUnreadMessages = useMemo(
    () =>
      debounce(() => {
        const processArr = Array.from(unreadMessagesRef.current.values());
        if (processArr.length === 0) return;
        markMessagesAsRead(refs.current.chat, processArr);
        processUnreadArrRef.current = processArr;
        const itemUpdated = processUnreadArrRef.current.find(
          (item) => item.message_id === replyMessage?.message?.message_id
        );
        if (itemUpdated) {
          setReplyMessage({ message: itemUpdated });
        }
        unreadMessagesRef.current.clear();
      }, 200),
    [markMessagesAsRead, refs, replyMessage?.message?.message_id, setReplyMessage]
  );

  const handleMarkUnReadMessages = useCallback(
    async (message: ChatMessage) => {
      if (unreadMessagesRef.current.has(message.id)) return;
      unreadMessagesRef.current.set(message.id, message);
      processUnreadMessages();
    },
    [processUnreadMessages]
  );

  const handleShortcut = async (type: ShortcutType) => {
    switch (type) {
      case ShortcutType.DOWN_UP:
        if (chatLastMessageId && chatLastMessageId > chat.last_read_message_id) {
          refs.current.updateChats([
            {
              chat_id: chat.chat_id,
              last_read_message_id: chatLastMessageId,
            },
          ]);
          if (hasMoreLast || chatLastMessageId - chat.last_read_message_id >= LIMIT) {
            firstIdRef.current = chatLastMessageId;
            setHasMoreHistory(false);
            setHasMoreLast(false);
            await handleRemoveMessages(messages);
            setHasMoreHistory(true);
            await loadData(DIRECTION.UP, true, true);
          }
          containerRef.current?.scrollIntoView({
            block: 'end',
          });
        }
        break;
      case ShortcutType.MARK: {
        const msg = mentionMsgs.at(-1)!;
        handleLinkedMsgId(msg.id, msg.message_id);
        break;
      }
    }
  };

  const handleRemoveMessages = useCallback(
    (messages: ChatMessage[]) => {
      const { removeMessages } = refs.current;
      return new Promise((resolve) => {
        const timerId = setTimeout(() => {
          removeMessages([
            {
              chat_id: chat.chat_id,
              messages: messages.map((message) => message.id),
            },
          ]);
          resolve(true);
          clearTimeout(timerId);
        }, 200);
      });
    },
    [chat.chat_id, refs]
  );

  const loadData = useCallback(
    async (
      direction: DIRECTION,
      include_self = false,
      isTop = false,
      last_msg_id?: number,
      lastIdRef_?: number
    ) => {
      setLoading(true);
      const { loadMessages, chat } = refs.current;
      const msg_id =
        last_msg_id ||
        (direction === DIRECTION.UP ? firstIdRef.current! : lastIdRef.current!);
      const result = await loadMessages({
        ...chat,
        limit: LIMIT * direction,
        msg_id,
        include_self,
        current_timestamp: serverTime.time,
      });
      const resData = result?.messages as ChatMessage[];
      if (resData?.length > 0) {
        if (direction === DIRECTION.UP) {
          firstIdRef.current = resData.reverse()?.[0]?.id;
          setHasMoreHistory(resData?.length === Math.abs(LIMIT));
          if (lastIdRef_) {
            lastIdRef.current = lastIdRef_;
          }
          if (
            messages.filter((message) => message.payload).length > 3 * LIMIT &&
            !isTop
          ) {
            const sliceMessages = messages.slice(messages.length - LIMIT);
            await handleRemoveMessages(sliceMessages);
            setHasMoreLast(true);
            lastIdRef.current = messages[messages.length - LIMIT - 1]?.id;
          }
        } else {
          lastIdRef.current = resData?.reverse()?.at(-1)?.id as number;
          setHasMoreLast(false);
          const timerId = setTimeout(() => {
            clearTimeout(timerId);
            setHasMoreLast(resData?.length === Math.abs(LIMIT));
          }, 60);
          if (
            messages.filter((message) => message.payload).length > 3 * LIMIT &&
            !isTop
          ) {
            const sliceMessages = messages.slice(0, LIMIT);
            await handleRemoveMessages(sliceMessages);
            setHasMoreHistory(true);
            firstIdRef.current = messages[LIMIT]?.id;
          }
        }
      } else {
        if (direction === DIRECTION.UP) {
          setHasMoreHistory(false);
        } else {
          setHasMoreLast(false);
        }
      }
      return new Promise((resolve) => {
        const timerId = setTimeout(() => {
          setLoading(false);
          resolve(true);
          clearTimeout(timerId);
        }, 200);
      });
    },
    [messages, refs, handleRemoveMessages]
  );

  refs.current.loadData = loadData;

  const atTopLoadData = useCallback(async () => {
    if (isInitialLoad.current) {
      isSelfSendRef.current = false;
      isLoadingTop.current = true;
      prevScrollInfoRef.current = {
        scrollTop: scrollRef.current?.scrollTop,
        scrollHeight: containerRef.current?.scrollHeight,
      };
      if (hasMoreHistory && !loading && firstIdRef.current) {
        await loadData(DIRECTION.UP);
      }
    }
  }, [hasMoreHistory, loadData, loading]);

  const atBottomLoadData = useCallback(async () => {
    if (isInitialLoad.current) {
      isSelfSendRef.current = false;
      isLoadingTop.current = false;
      if (hasMoreLast && !loading && lastIdRef.current) {
        await loadData(DIRECTION.DOWN);
      }
    }
  }, [hasMoreLast, loadData, loading]);

  const getInitialData = useCallback(async () => {
    const {
      chat,
      clearAllMessages,
      chatLastMessageId,
      loadData,
      messages,
      updateStateMessageAsRead,
    } = refs.current;

    if (chat.chat_id === chatsState.activatedChat?.chat_id) {
      const hasNewMessage =
        chat?.last_read_message_id === chatLastMessageId &&
        chat?.last_read_message_id !== chat?.last_read_new_message_id;

      updateStateMessageAsRead(chat);

      if (
        (chatLastMessageId && chat?.last_read_message_id < chatLastMessageId) ||
        !messages.length ||
        chat?.last_read_message_id === -1 ||
        hasNewMessage
      ) {
        clearAllMessages([
          {
            chat_id: chat.chat_id,
            messages: [],
          },
        ]);

        lastIdRef.current = chat.last_read_message_id || -1;
        if (chat.last_read_message_id !== -1) {
          firstIdRef.current = chat.last_read_message_id;
          await loadData(DIRECTION.UP, true, true);
        } else {
          setHasMoreHistory(false);
        }
        await loadData(DIRECTION.DOWN, false, true);
        setCanInitScroll(true);
      }
      isInitialLoad.current = true;
      setInitialLoading(true);
    }
  }, [chatsState.activatedChat?.chat_id, refs]);

  const initScroll = useCallback(() => {
    let initScrollIndex = 0;
    let initScrollAlign: ScrollLogicalPosition = 'start';

    if (chat.last_read_message_id === -1) {
      return {
        initScrollIndex,
        initScrollAlign,
      };
    }
    if (allMessages.length > 0) {
      const lastReadMessageIndex = allMessages.findLastIndex(
        (message) => isNumber(message.id) && message.id <= chat.last_read_message_id
      );
      const initHasNewMessage = lastReadMessageIndex < allMessages.length - 1;
      if (lastReadMessageIndex === -1) {
        initScrollIndex = 0;
        initScrollAlign = 'start';
      } else {
        initScrollIndex = lastReadMessageIndex;
        initScrollAlign = initHasNewMessage ? 'start' : 'end';
      }
    }
    return {
      initScrollIndex,
      initScrollAlign,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMessages]);

  const handleHighlightLinked = useCallback((link_msg_id: string) => {
    setHighlightLinked(link_msg_id);
    const timerId = setTimeout(() => {
      clearTimeout(timerId);
      setHighlightLinked('');
    }, 3000);
  }, []);

  const handleLinkedMsgId = useCallback(
    async (id: number, message_id: string) => {
      if (message_id) {
        const element = document.getElementById(`key-${message_id}`);
        if (element) {
          scrollRef.current!.scrollTop = element.offsetTop - 60;
          handleHighlightLinked(message_id);
        } else {
          await handleRemoveMessages(messages);
          setHasMoreLast(true);
          await loadData(DIRECTION.UP, true, false, id, id);
          await loadData(DIRECTION.DOWN, false, true);
          const el = document.getElementById(`key-${message_id}`);
          if (el) {
            scrollRef.current!.scrollTop = el.offsetTop - 60;
            handleHighlightLinked(message_id);
          } else {
            Dialog({
              title: 'The message has been destroyed.',
              confirmText: 'Back',
              showCancel: false,
            });
          }
        }
      }
    },
    [handleHighlightLinked, handleRemoveMessages, loadData, messages]
  );

  // TODO amo: retryLoadMessages
  const retryLoadMessages = useCallback(() => {}, []);

  useLayoutEffect(() => {
    let frameId: number;
    let lastReadMsgTooHighId: number;
    if (canInitScroll) {
      const { initScrollIndex, initScrollAlign } = initScroll();
      frameId = requestAnimationFrame(() => {
        const element = containerRef.current?.children[initScrollIndex + 1];
        if (element) {
          element.scrollIntoView({
            block: initScrollAlign,
          });
          const newMessageDividerPaddingTop = 60;
          if (
            scrollRef.current &&
            element instanceof HTMLElement &&
            element.offsetHeight > newMessageDividerPaddingTop
          ) {
            lastReadMsgTooHighId = window.setTimeout(() => {
              scrollRef.current!.scrollTop +=
                element.offsetHeight - newMessageDividerPaddingTop;
            }, 20);
          }
          if (initScrollAlign === 'end') {
            isNearAtBottomRef.current = true;
          }
          setCanInitScroll(false);
        }
      });
    }
    return () => {
      cancelAnimationFrame(frameId);
      cancelAnimationFrame(lastReadMsgTooHighId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canInitScroll]);

  useLayoutEffect(() => {
    if (!isInitialLoad.current) {
      const { chatsState, chat } = refs.current;
      chatsState.updateChats([
        {
          chat_id: chat.chat_id,
          loading: true,
        },
      ]);
      getInitialData().finally(() => {
        chatsState.updateChats([
          {
            chat_id: chat.chat_id,
            loading: false,
          },
        ]);
      });
    }
  }, [chat.chat_id, getInitialData, refs]);

  const handleRemoveOrScrollBottom = useCallback(async () => {
    if (
      hasMoreLast ||
      (chatLastMessageId && chatLastMessageId - chat.last_read_message_id >= LIMIT)
    ) {
      firstIdRef.current = lastMessage?.id as number;
      setHasMoreHistory(false);
      setHasMoreLast(false);
      await handleRemoveMessages(messages);
      setHasMoreHistory(true);
      await loadData(DIRECTION.UP, true, true, lastMessage?.id);
    }
    containerRef.current?.scrollIntoView({
      block: 'end',
    });
  }, [
    chat.last_read_message_id,
    chatLastMessageId,
    handleRemoveMessages,
    hasMoreLast,
    lastMessage?.id,
    loadData,
    messages,
  ]);

  useEffect(() => {
    let timer: number;
    if (!lastMessage) return;
    const isSelfSend = lastMessage.category === ChatMessageCategory.SEND;
    if (isSelfSend && chatLastMessageId && chatLastMessageId === lastMessage.id) {
      updateStateMessageAsRead(chat);
      isSelfSendRef.current = true;
      if (!isNearAtBottomRef.current && isInitialLoad.current) {
        handleRemoveOrScrollBottom();
      } else {
        timer = requestAnimationFrame(() => {
          containerRef.current?.scrollIntoView({
            block: 'end',
          });
        });
      }
    } else if (isNearAtBottomRef.current) {
      timer = requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView({
          block: 'end',
        });
      });
    }
    return () => {
      cancelAnimationFrame(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage?.id, chatLastMessageId]);

  useEffect(() => {
    if (chat.chat_id === chatsState.oldActivatedChat?.chat_id) {
      isSelfSendRef.current = false;
      isNearAtBottomRef.current = false;
      setInitialLoading(false);
      isInitialLoad.current = false;
    }
  }, [chat.chat_id, chatsState.oldActivatedChat?.chat_id]);

  useEffect(() => {
    return () => {
      processUnreadMessages.flush();
      processUnreadMessages.cancel();
    };
  }, [processUnreadMessages]);

  useEffect(() => {
    if (prevScrollInfoRef.current && isLoadingTop.current) {
      scrollRef.current!.scrollTop =
        prevScrollInfoRef.current.scrollTop +
        containerRef.current?.scrollHeight -
        prevScrollInfoRef.current.scrollHeight;
      isLoadingTop.current = false;
    }
  }, [allMessages]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          atTopLoadData();
        }
      },
      {
        threshold: 0.1,
        root: scrollRef.current,
        rootMargin: `${scrollRef.current?.clientHeight}px 0px 0px 0px`,
      }
    );
    if (loadingTopRef.current) {
      observer.observe(loadingTopRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [atTopLoadData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          atBottomLoadData();
        }
      },
      {
        threshold: 0.1,
        root: scrollRef.current,
        rootMargin: `0px 0px ${scrollRef.current?.clientHeight}px 0px`,
      }
    );
    if (loadingBottomRef.current) {
      observer.observe(loadingBottomRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [atBottomLoadData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (isInitialLoad.current) {
          isNearAtBottomRef.current = entry.isIntersecting;
        }
      },
      { threshold: 0.5 }
    );
    if (nearBottomRef.current) {
      observer.observe(nearBottomRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [hasMoreLast]);

  useEffect(() => {
    if (chat.chat_id === chatsState.activatedChat?.chat_id) {
      const friend = contacts.find((contact) => contact.user_id === chat.user.userId);
      setIsFriend(!!friend);
    }
  }, [chat.chat_id, chat.user.userId, chatsState.activatedChat?.chat_id, contacts]);

  return (
    <div
      className={cls('scrollbar_hover', classes.chatMessagesWrapper, className)}
      ref={scrollRef}
    >
      {!initialLoading && (
        <div className={classes.initialLoading}>
          <Loading bold />
        </div>
      )}
      <DebugTooltip info={allMessages} excludeFields={['avatar', 'expect_read_list']}>
        <div style={{ position: 'absolute', width: 20, height: 20 }}></div>
      </DebugTooltip>
      <div className={cls(classes.chatMessages, className)} ref={containerRef}>
        <div
          className={classes.header}
          style={{ display: hasMoreHistory ? 'block' : 'none' }}
          ref={loadingTopRef}
        >
          {loading && <Loading className={classes.loading} />}
        </div>

        {allMessages.map((message, index) => {
          const dividers = handleDividerMessage(
            chat,
            allMessages?.[index - 1],
            message,
            index,
            !isSelfSendRef.current,
            hasMoreHistory,
            hiddenNewMessages
          );
          if (
            !dividers ||
            (hasMoreLast &&
              message?.id > lastIdRef.current &&
              message?.payload_type === CustomChatMessagePayloadType.ADD_TO_CONTACTS)
          ) {
            return;
          }
          const hiddenFirstItemDivider = !(dividers && index === 0 && hasMoreHistory);
          return (
            <div
              data-index={index}
              data-id={message.id}
              key={`${message.id}_${message.message_id}_${message.cli_msg_id}`}
            >
              {hiddenFirstItemDivider &&
                dividers?.map((item: Divider, index: number) => (
                  <div className={classes.messageItem} key={index}>
                    <MessageRenderer
                      isFriend={isFriend}
                      message={item as any}
                      chat={chat}
                      merge={false}
                      chatGroup={chatGroup}
                      chatMessages={chatMessages}
                    />
                  </div>
                ))}
              {!!message?.id && (
                <DebugTooltip info={message}>
                  <ObserverItem
                    chat={chat}
                    message={message as ChatMessage}
                    shouldObserver={
                      // isNumber(message.id) && message.status !== ChatMessageStatus.READ
                      isNumber(message.id)
                    }
                    onMessageShow={handleMarkUnReadMessages}
                  >
                    <MessageRenderer
                      isFriend={isFriend}
                      message={message as ChatMessage}
                      highlightLinked={highlightLinked}
                      handlers={{
                        handleLinkedMsgId,
                        allMessages,
                        triggerReadArr: processUnreadArrRef.current,
                      }}
                      chat={chat}
                      chatGroup={chatGroup}
                      chatMessages={chatMessages}
                      setReplyMessage={setReplyMessage}
                      merge={
                        allMessages[index - 1]?.owner_id === message.owner_id &&
                        !dividers?.length &&
                        [
                          ChatMessagePayloadType.TEXT,
                          ChatMessagePayloadType.ENCRYPTED_TEXT,
                          ChatMessagePayloadType.IMAGE,
                          ChatMessagePayloadType.FILE,
                        ].includes(
                          allMessages[index - 1]?.payload_type as ChatMessagePayloadType
                        )
                      }
                      onClickRetry={retryLoadMessages}
                    />
                  </ObserverItem>
                </DebugTooltip>
              )}
            </div>
          );
        })}
        {hasMoreLast && (
          <div className={classes.footer} ref={loadingBottomRef}>
            {loading && <Loading className={classes.loading} />}
          </div>
        )}
      </div>
      {!hasMoreLast && <div className={classes.nearBottom} ref={nearBottomRef}></div>}

      <AffixShortcut
        newMessageCounts={isNearAtBottomRef.current ? 0 : unreadCount}
        markCounts={mentionMsgs.length}
        onClick={handleShortcut}
      />
    </div>
  );
}

export default ChatMessages;
